/**
 * Standardized playback lifecycle (Sprint 6A).
 *
 * Initialize → ResolveProvider → CreateProvider → InitializeProvider →
 * Ready → Playing → Paused → Ended → Destroyed
 *
 * Maps onto existing PlaybackState for UI while exposing a coarser platform phase.
 */
import { PlaybackState } from "./PlaybackState";
import { PlaybackPlatformEvent } from "./PlaybackPlatformEvents";

export var LifecyclePhase = Object.freeze({
  UNINITIALIZED: "uninitialized",
  INITIALIZE: "initialize",
  RESOLVE_PROVIDER: "resolve_provider",
  CREATE_PROVIDER: "create_provider",
  INITIALIZE_PROVIDER: "initialize_provider",
  READY: "ready",
  PLAYING: "playing",
  PAUSED: "paused",
  BUFFERING: "buffering",
  SEEKING: "seeking",
  ENDED: "ended",
  ERROR: "error",
  DESTROYED: "destroyed",
});

/**
 * @param {string} playbackState
 * @returns {string} LifecyclePhase
 */
export function mapPlaybackStateToLifecycle(playbackState) {
  switch (playbackState) {
    case PlaybackState.IDLE:
      return LifecyclePhase.UNINITIALIZED;
    case PlaybackState.LOADING:
      return LifecyclePhase.INITIALIZE_PROVIDER;
    case PlaybackState.READY:
      return LifecyclePhase.READY;
    case PlaybackState.PLAYING:
      return LifecyclePhase.PLAYING;
    case PlaybackState.PAUSED:
      return LifecyclePhase.PAUSED;
    case PlaybackState.BUFFERING:
      return LifecyclePhase.BUFFERING;
    case PlaybackState.SEEKING:
      return LifecyclePhase.SEEKING;
    case PlaybackState.ENDED:
      return LifecyclePhase.ENDED;
    case PlaybackState.ERROR:
      return LifecyclePhase.ERROR;
    default:
      return LifecyclePhase.UNINITIALIZED;
  }
}

/**
 * @param {object} [options]
 * @param {function} [options.onChange] (phase, meta) => void
 * @param {object} [options.bus] PlaybackEventBus
 */
export function createLifecycleManager(options) {
  options = options || {};
  var phase = LifecyclePhase.UNINITIALIZED;
  var history = [];

  function getPhase() {
    return phase;
  }

  function getHistory() {
    return history.slice();
  }

  /**
   * @param {string} next
   * @param {object} [meta]
   */
  function transition(next, meta) {
    if (!next || next === phase) {
      return phase;
    }
    var prev = phase;
    phase = next;
    var entry = {
      previous: prev,
      phase: next,
      at: Date.now(),
      meta: meta || null,
    };
    history.push(entry);
    if (history.length > 50) {
      history.shift();
    }
    if (typeof options.onChange === "function") {
      try {
        options.onChange(next, entry);
      } catch (e) {
        /* isolate */
      }
    }
    if (options.bus && typeof options.bus.publish === "function") {
      options.bus.publish(PlaybackPlatformEvent.LIFECYCLE_CHANGED, entry);
    }
    return phase;
  }

  function syncFromPlaybackState(playbackState, meta) {
    return transition(mapPlaybackStateToLifecycle(playbackState), meta);
  }

  function markDestroyed() {
    return transition(LifecyclePhase.DESTROYED);
  }

  function reset() {
    phase = LifecyclePhase.UNINITIALIZED;
    history = [];
  }

  return {
    getPhase: getPhase,
    getHistory: getHistory,
    transition: transition,
    syncFromPlaybackState: syncFromPlaybackState,
    markDestroyed: markDestroyed,
    reset: reset,
    phases: LifecyclePhase,
  };
}

export default createLifecycleManager;
